import "./TipDetail.css";
import { buildPageTitle } from "../../utils/pageTitle";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { IoIosCall } from "react-icons/io";
import axiosInstance from "../../api/axiosInstance";
import { getPreloadedData, PreloadedScript } from "../../utils/preloadedData";
import { ensureMetaDescription } from "../../utils/metaDescription";
import { formatDate } from "../../utils/formatDate";

// Helper function to create a URL-friendly slug from the title (matches the format used by Tips.js links)
const createSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

// Helper to build a safe, truncated meta description from plain text
const truncateForMeta = (text, maxLength = 160) => {
  if (!text) return "";
  const plain = text.replace(/\s+/g, " ").trim();
  return plain.length > maxLength ? plain.slice(0, maxLength).trim() + "..." : plain;
};

function TipDetail() {
  const { id, title } = useParams(); // Extract both id and title from URL
  const navigate = useNavigate();
  const [tip, setTip] = useState(() => getPreloadedData("__PRELOADED_TIP__"));
  // Not loading when the prerendered snapshot already supplied the tip, so the first client render matches it.
  const [isLoading, setIsLoading] = useState(() => getPreloadedData("__PRELOADED_TIP__") === null);
  const [error, setError] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchTip = async () => {
      setError(null);
      try {
        const response = await axiosInstance.get(`/api/tips/${id}`);
        const data = response.data;
        console.log("Detail API Response:", data); // Debug API response
        setTip(data);
      } catch (error) {
        if (error.response?.status === 404) {
          setError("Tip not found. Please check the URL or try again later.");
        } else {
          setError("Failed to load tip details. Please try again later.");
        }
        console.error("Error fetching tip details:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTip();
  }, [id]);

  const handleBackClick = () => {
    navigate("/tips");
  };

  // Normalize image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return `${baseUrl}/images/default-image.jpg`;
    return `${baseUrl}${imageUrl.startsWith("/") ? imageUrl : "/" + imageUrl}`;
  };

  // Display loader while fetching data
  if (isLoading) {
    return (
      <div className="loader-container">
        <div className="loader">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error_message">
        {error}
        {error.includes("Tip not found") && (
          <button className="btn_back_blog" onClick={handleBackClick} aria-label="Back to tips">
            <IoIosCall /> Go back to Tips
          </button>
        )}
      </div>
    );
  }

  if (!tip) return <div>Tip not found.</div>;

  const pageTitle = buildPageTitle(tip.title);
  const pageDescription = ensureMetaDescription(truncateForMeta(tip.text), tip.title, "Tips and insights from 360 SynergyTech.");
  const canonicalUrl = `https://360synergytech.com/tips/${id}/${createSlug(tip.title)}`;
  const ogImage = getImageUrl(tip.image);

  return (
    <div className="main_div_TipDetail">
      <PreloadedScript id="__PRELOADED_TIP__" data={tip} />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://360synergytech.com/" },
              { "@type": "ListItem", position: 2, name: "Tips", item: "https://360synergytech.com/tips" },
              { "@type": "ListItem", position: 3, name: tip.title, item: canonicalUrl },
            ],
          })}
        </script>
      </Helmet>
      <div className="row p-0 m-0">
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
        <div className="col-lg-8 col-md-10 col-sm-12 col-12">
          <div className="row m-0 p-0">
            <div className="col-lg-8 col-md-12 col-sm-12 col-12">
              {/* <button className="btn_back_blog" onClick={handleBackClick} aria-label="Back to tips">
                <IoIosCall />
              </button> */}
              <img
                src={getImageUrl(tip.image)}
                className="img_bg_tipdetail"
                alt={tip.title}
                onError={(e) => {
                  console.log(`Image failed to load for ${tip.title}:`, e.target.src);
                  e.target.onerror = null;
                  e.target.src = `${baseUrl}/images/default-image.jpg`;
                }}
              />
              <div className="grey_detail_div">
                <h1 className="heading_orange_tipdetail" style={{ marginTop: 0, marginBottom: "1rem" }}>{tip.title}</h1>
                <p className="para_black_tipdetail">{tip.text}</p>
                <p className="heading_orange_tipdetail">Service Info</p>
                <p className="para_black_tipdetail">{tip.service_name}</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12 col-12">
              <div className="project_info_div">
                <p className="project_info_heading">Tip Info</p>
                <div className="row p-0 m-0">
                  <div className="col-6">
                    <p className="detail_heading_project_info">Posted By:</p>
                  </div>
                  <div className="col-6">
                    <p className="detail_heading_project_info1">{tip.posted_name}</p>
                  </div>
                </div>
                <div className="row p-0 m-0">
                  <div className="col-6">
                    <p className="detail_heading_project_info">Date:</p>
                  </div>
                  <div className="col-6">
                    <p className="detail_heading_project_info1">
                      {formatDate(tip.posted_date)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="blue_div_questions">
                <div className="d-flex justify-content-center">
                  <button className="btn_call_questions" aria-label="Call us">
                    <IoIosCall />
                  </button>
                </div>
                <p className="cll_us_question_heading">Have any Questions? Call us Today!</p>
                <p className="cll_us_phone">+92 324 9925737</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-1 col-sm-12 col-12"></div>
      </div>
    </div>
  );
}

export default TipDetail;